import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, X, Wallet } from 'lucide-react';
import * as XLSX from 'xlsx';
import styles from './WalletLedgerDetail.module.css';
import Header from '../../../Header';
import { local_signage } from '../../../Const';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const WalletLedgerDetail = () => {
    const { mobile, customerName } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Read query params passed from WalletLedger
    const qp = new URLSearchParams(location.search);
    const initFrom = qp.get('from') || '2025-01-01';
    const initTo = qp.get('to') || new Date().toISOString().split('T')[0];
    const initFy = qp.get('fy') || null;

    const [fromDate, setFromDate] = useState(dayjs(initFrom));
    const [toDate, setToDate] = useState(dayjs(initTo));

    // Financial Year
    const [finYears, setFinYears] = useState([]);
    const [selectedFinYear, setSelectedFinYear] = useState(initFy);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Presto Wallet modal
    const [prestoModalOpen, setPrestoModalOpen] = useState(false);
    const [prestoData, setPrestoData] = useState([]);
    const [prestoLoading, setPrestoLoading] = useState(false);
    const [prestoError, setPrestoError] = useState(null);

    // Fetch financial years
    useEffect(() => {
        const authToken = localStorage.getItem('AuthorizationToken');
        fetch(`${local_signage}/api/v1/fetchfinancialyear`, {
            headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
        })
            .then(r => r.json())
            .then(years => {
                const active = (years || []).filter(y => y.Deleted === 'N');
                setFinYears(active);
                // If no FY passed from parent, auto-select based on today
                if (!initFy) {
                    const today = new Date();
                    const current = active.find(y => {
                        const from = new Date(y.FromDate);
                        const to = new Date(y.ToDate);
                        return today >= from && today <= to;
                    }) || active[0];
                    if (current) {
                        setSelectedFinYear(current.FinYearId);
                        setFromDate(dayjs(current.FromDate));
                        setToDate(dayjs(current.ToDate));
                    }
                }
            })
            .catch(console.error);
    }, []);

    const handleFinYearChange = (e) => {
        const id = e.target.value;
        const fy = finYears.find(y => y.FinYearId === id);
        if (fy) {
            setSelectedFinYear(fy.FinYearId);
            setFromDate(dayjs(fy.FromDate));
            setToDate(dayjs(fy.ToDate));
        }
    };

    const fyMin = selectedFinYear ? dayjs((finYears.find(y => y.FinYearId === selectedFinYear)?.FromDate || '')) : null;
    const fyMax = selectedFinYear ? dayjs((finYears.find(y => y.FinYearId === selectedFinYear)?.ToDate || '')) : null;


    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem("AuthorizationToken");
            const headers = {
                'Authorization': `Bearer ${authToken}` || authToken,
                'Content-Type': 'application/json'
            };

            const formattedFrom = fromDate ? fromDate.format('YYYYMMDD') : '';
            const formattedTo = toDate ? toDate.format('YYYYMMDD') : '';

            const url = `${local_signage}/api/v1/reportwallettransactionbymobile?Mobile=${mobile}&FilterbyChannel=ALL&FromDate=${formattedFrom}&ToDate=${formattedTo}`;
            const response = await fetch(url, { method: 'GET', headers });

            if (!response.ok) throw new Error(`API error! status: ${response.status}`);

            const result = await response.json();
            const rawData = Array.isArray(result) ? result : (result.data || []);

            const sortedData = [...rawData].sort((a, b) => new Date(a.BusinessDate) - new Date(b.BusinessDate));

            let currentBalance = 0;
            const processedData = sortedData.map(item => {
                const opening = parseFloat(item.Opening) || 0;
                const posCredit = parseFloat(item.POSCredit) || 0;
                const webCredit = parseFloat(item.WebCredit) || 0;
                const posDebit = parseFloat(item.POSdebit) || 0;
                const webDebit = parseFloat(item.Webdebit) || 0;
                currentBalance = currentBalance + opening + posCredit + webCredit - posDebit - webDebit;
                return {
                    date: item.BusinessDate,
                    branchId: item.BranchId || '-',
                    transId: item.TransId || '-',
                    opening,
                    posCredit,
                    webCredit,
                    posDebit,
                    webDebit,
                    balance: currentBalance
                };
            });

            setData(processedData);

        } catch (err) {
            setError(err.message);
            console.error("Error fetching detail ledger:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchPrestoWallet = useCallback(async () => {
        setPrestoLoading(true);
        setPrestoError(null);
        try {
            const res = await fetch(
                `https://ibretailapi.abisaio.com/api/PrestoOrder/GetPrestoWalletTransactionsbyMobile/${mobile}`
            );
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const result = await res.json();
            const arr = Array.isArray(result) ? result : (result.data || []);
            const sorted = [...arr].sort((a, b) => new Date(a.TransTime) - new Date(b.TransTime));
            setPrestoData(sorted);
        } catch (err) {
            setPrestoError(err.message);
        } finally {
            setPrestoLoading(false);
        }
    }, [mobile]);

    const openPrestoModal = () => {
        setPrestoModalOpen(true);
        if (prestoData.length === 0) fetchPrestoWallet();
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${day}-${month}-${year}`;
    };

    const finalBalance = data.length > 0 ? data[data.length - 1].balance : null;

    const exportToXLSX = () => {
        if (!data.length) return;
        const exportData = data.map((row) => ({
            'Date': formatDate(row.date),
            'Branch ID': row.branchId,
            'Trans ID': row.transId,
            'Opening (₹)': row.opening || 0,
            'POS Credit (₹)': row.posCredit || 0,
            'Web Credit (₹)': row.webCredit || 0,
            'POS Debit (₹)': row.posDebit || 0,
            'Web Debit (₹)': row.webDebit || 0,
            'Balance (₹)': row.balance
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Wallet Ledger');
        XLSX.writeFile(workbook, `wallet_ledger_${mobile}.xlsx`);
    };

    const exportPrestoToXLSX = () => {
        if (!prestoData.length) return;
        const exportData = prestoData.map((row) => {
            const dt = new Date(row.TransTime);
            return {
                'Date': dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                'Time': dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
                'Trans Type': row.AcctTransType,
                'Opening (₹)': (row.Opening / 100).toFixed(2),
                'Amount (₹)': (row.Amount / 100).toFixed(2),
                'Closing (₹)': (row.Closing / 100).toFixed(2),
                'Transaction ID': row.TransId
            };
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Pro Online Wallet');
        XLSX.writeFile(workbook, `pro_wallet_ledger_${mobile}.xlsx`);
    };

    return (
        <>
            <Header />
            <div className={styles.detail_dashboard}>
                <div className={styles.header_section}>
                    <div className={styles.header_top_row}>
                        <div className={styles.header_title}>
                            <button className={styles.back_button} onClick={() => navigate('/WalletLedger')} title="Back to Wallet Ledger">
                                <ArrowLeft size={20} />
                            </button>
                            <div className={styles.title_text}>
                                <h2>{decodeURIComponent(customerName)}</h2>
                                <p>Mobile: {mobile} | Detailed Ledger</p>
                            </div>
                        </div>

                        <div className={styles.header_extras}>
                            {finalBalance !== null && (
                                <div className={styles.balance_card}>
                                    <div className={styles.balance_info}>
                                        <span className={styles.balance_label}>Final Balance</span>
                                        <span className={`${styles.balance_value} ${finalBalance < 0 ? styles.negative : styles.positive}`}>
                                            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(finalBalance)}
                                        </span>
                                    </div>
                                    <button className={styles.presto_btn} onClick={openPrestoModal} title="View Pro Online Wallet Ledger">
                                        <Wallet size={14} />
                                        <span>PRO Wallet</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                        <div className={styles.filter_row} style={{ flexWrap: 'nowrap' }}>
                            <div className={styles.filter_group}>
                                <label>Financial Year</label>
                                <select
                                    value={selectedFinYear || ''}
                                    onChange={handleFinYearChange}
                                    className={styles.filter_input}
                                    style={{ height: '38px' }}
                                >
                                    {finYears.map(fy => (
                                        <option key={fy.FinYearId} value={fy.FinYearId}>
                                            FY {fy.FinYearId.slice(0, 2)}-{fy.FinYearId.slice(2)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div className={styles.filter_group}>
                                        <label>From Date</label>
                                        <DatePicker
                                            value={fromDate}
                                            onChange={(v) => setFromDate(v)}
                                            format="DD-MM-YYYY"
                                            readOnly={!!selectedFinYear}
                                            minDate={fyMin}
                                            maxDate={fyMax}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    sx: { 
                                                        '& .MuiInputBase-root': { height: '38px', backgroundColor: selectedFinYear ? '#f8f9fa' : '#fff' },
                                                        width: '145px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className={styles.filter_group}>
                                        <label>To Date</label>
                                        <DatePicker
                                            value={toDate}
                                            onChange={(v) => setToDate(v)}
                                            format="DD-MM-YYYY"
                                            minDate={fyMin}
                                            maxDate={fyMax}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    sx: { 
                                                        '& .MuiInputBase-root': { height: '38px', backgroundColor: '#fff' },
                                                        width: '145px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div >
                            </LocalizationProvider>
                            <div className={styles.filter_group} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                                <button className={styles.fetch_button} onClick={fetchData} disabled={loading} style={{ height: '38px', alignSelf: 'flex-end' }}>
                                    {loading ? 'Fetching...' : 'Fetch Data'}
                                </button>
                            </div>
                        </div>
                </div>

                <div className={styles.table_section} style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', padding: '15px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #eaeaea' }}>
                        <div style={{ fontSize: '13px', color: '#666' }}>Total: {data.length} transactions</div>
                        <button className={styles.export_btn} onClick={exportToXLSX} disabled={data.length === 0}>Export XLSX</button>
                    </div>

                    {loading && (
                        <div className={styles.loader_container}>
                            <div className={styles.loader}></div>
                            <p>Loading transactions...</p>
                        </div>
                    )}
                    {!loading && error && <div className={styles.no_data}><p>Error: {error}</p></div>}
                    {!loading && !error && data.length === 0 && <div className={styles.no_data}><p>No transactions found.</p></div>}
                    {!loading && !error && data.length > 0 && (
                        <div className={styles.table_container} style={{ overflowY: 'auto', maxHeight: '60vh' }}>
                            <table className={styles.data_table}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Branch ID</th>
                                        <th>Trans ID</th>
                                        <th>Opening</th>
                                        <th>POS Credit</th>
                                        <th>Web Credit</th>
                                        <th>POS Debit</th>
                                        <th>Web Debit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(row.date)}</td>
                                            <td>{row.branchId}</td>
                                            <td>{row.transId}</td>
                                            <td className={`${styles.amount_cell} ${styles.credit_text}`}>{row.opening > 0 ? formatCurrency(row.opening) : '-'}</td>
                                            <td className={`${styles.amount_cell} ${styles.credit_text}`}>{row.posCredit > 0 ? formatCurrency(row.posCredit) : '-'}</td>
                                            <td className={`${styles.amount_cell} ${styles.credit_text}`}>{row.webCredit > 0 ? formatCurrency(row.webCredit) : '-'}</td>
                                            <td className={`${styles.amount_cell} ${styles.debit_text}`}>{row.posDebit > 0 ? formatCurrency(row.posDebit) : '-'}</td>
                                            <td className={`${styles.amount_cell} ${styles.debit_text}`}>{row.webDebit > 0 ? formatCurrency(row.webDebit) : '-'}</td>
                                            <td className={`${styles.amount_cell} ${row.balance < 0 ? styles.balance_negative : styles.balance_positive}`}>
                                                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(row.balance)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* PRO Wallet Modal */}
            {prestoModalOpen && (
                <div className={styles.modal_overlay} onClick={() => setPrestoModalOpen(false)}>
                    <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
                        <div className={styles.modal_header}>
                            <div className={styles.modal_title}>
                                <Wallet size={18} style={{ marginRight: '8px', color: '#667eea' }} />
                                <span>Pro Online Wallet Ledger</span>
                                <span className={styles.modal_mobile}>{mobile}</span>
                            </div>
                            {!prestoLoading && !prestoError && prestoData.length > 0 && (() => {
                                const closing = prestoData[prestoData.length - 1].Closing;
                                const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v / 100);
                                return (
                                    <div style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                                        padding: '4px 12px', background: 'rgba(255,255,255,0.12)',
                                        borderRadius: '8px', borderLeft: '3px solid rgba(255,255,255,0.4)'
                                    }}>
                                        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Final Closing Balance</span>
                                        <span style={{ fontSize: '18px', fontWeight: 800, color: closing >= 0 ? '#86efac' : '#fca5a5' }}>
                                            {fmt(closing)}
                                        </span>
                                    </div>
                                );
                            })()}
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button 
                                    className={styles.modal_refresh_btn} 
                                    onClick={exportPrestoToXLSX} 
                                    disabled={prestoLoading || prestoData.length === 0}
                                    style={{ background: '#10b981', borderColor: '#10b981' }}
                                >
                                    💾 Excel
                                </button>
                                <button className={styles.modal_refresh_btn} onClick={fetchPrestoWallet} disabled={prestoLoading}>
                                    {prestoLoading ? 'Loading…' : '↻ Refresh'}
                                </button>
                                <button className={styles.modal_close_btn} onClick={() => setPrestoModalOpen(false)}>
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        <div className={styles.modal_body}>
                            {prestoLoading && (
                                <div className={styles.loader_container}>
                                    <div className={styles.loader}></div>
                                    <p>Loading Pro transactions…</p>
                                </div>
                            )}
                            {!prestoLoading && prestoError && (
                                <div className={styles.no_data}><p>Error: {prestoError}</p></div>
                            )}
                            {!prestoLoading && !prestoError && prestoData.length === 0 && (
                                <div className={styles.no_data}><p>No Pro wallet transactions found.</p></div>
                            )}
                            {!prestoLoading && !prestoError && prestoData.length > 0 && (
                                <table className={styles.data_table}>
                                    <thead>
                                        <tr>
                                            <th>Date &amp; Time</th>
                                            <th>Opening (₹)</th>
                                            <th>Credit (₹)</th>
                                            <th>Debit (₹)</th>
                                            <th>Closing (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {prestoData.map((row, idx) => {
                                            const isCredit = row.AcctTransType === 'CREDIT';
                                            const dt = new Date(row.TransTime);
                                            const day = String(dt.getDate()).padStart(2, '0');
                                            const month = String(dt.getMonth() + 1).padStart(2, '0');
                                            const year = String(dt.getFullYear()).slice(-2);
                                            const dateStr = `${day}-${month}-${year}`;
                                            const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                                            const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v / 100);
                                            return (
                                                <tr key={row.TransId}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <div style={{ fontWeight: 600 }}>{dateStr}</div>
                                                        <div style={{ fontSize: '11px', color: '#888' }}>{timeStr}</div>
                                                    </td>
                                                    <td className={`${styles.amount_cell}`}>{idx === 0 ? fmt(row.Opening) : '-'}</td>
                                                    <td className={`${styles.amount_cell} ${styles.credit_text}`}>{isCredit ? fmt(row.Amount) : '-'}</td>
                                                    <td className={`${styles.amount_cell} ${styles.debit_text}`}>{!isCredit ? fmt(row.Amount) : '-'}</td>
                                                    <td className={`${styles.amount_cell} ${styles.balance_positive}`}>{fmt(row.Closing)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default WalletLedgerDetail;
